import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { isMetricDeltaVisible, isMetricVisible } from '@/features/shared/metricVisibility';
import { resolveEntityTags } from '@/features/shared/tags';
import {
  CloneActionsCell,
  CloneCellContainer,
} from '@/features/tests/components/testsColumns.cloneCells';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import {
  Box,
  Chip,
  formatDuration,
  formatStatusLabel,
  getStatusTextColor,
  Inline,
  Link,
  resolveStatusChipTone,
  Text,
  Tooltip,
} from '@/ui';
import { getStringFilterOperators } from '@/ui/composites/grid/gridOperators';
import { getStatusIconComponent, StackedCountBar, TableTextCell } from '@/ui/composites/table';
import { buildMetricColumns, type MetricColumnKey } from '@/ui/composites/table/tableUtils';
import { formatDate } from '@/ui/formatters/formatDate';
import { formatRelativeTime } from '@/ui/formatters/formatRelativeTime';
import { TestsIcon } from '@/ui/primitives';
import { SuiteLatestStatusPie } from './SuiteLatestStatusPie';
import {
  CLONE_TOOLTIP_SLOT_PROPS,
  LATEST_META_DETAIL_ROW_SX,
  LATEST_META_DETAIL_TEXT_SX,
  LATEST_META_DETAIL_VALUE_SX,
  RELATIVE_TIME_TEXT_SX,
  resolveLatestChipBackground,
  resolveLatestChipForeground,
  SUITE_NAME_TO_TAGS_GAP,
} from './suitesColumns.latestCellStyles';
import { buildSuitesSparklineColumns } from './suitesColumns.sparklineColumns';
import type { TagColor } from '@/shared/data/tags';
import type { GridColDef, GridRenderCellParams } from '@/ui/primitives';
import type { MetricThresholdGroup, SuiteExecutionStatus, SuiteListItemDto } from '@flowqa/shared';

type SuitesColumnsOptions = {
  includeLatestTestsColumn?: boolean;
  includeLastExecutedColumn?: boolean;
  latestAsMeta?: boolean;
  hideLatestPies?: boolean;
  includeTagsInNameColumn?: boolean;
  includeTagsColumn?: boolean;
  includeActionsColumn?: boolean;
  onDeleteRow?: (row: SuiteListItemDto) => void;
  deleteRowPendingId?: string | null;
  tagColorByName?: Record<string, TagColor>;
  onUpdateRowTagsColor?: (input: {
    row: SuiteListItemDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId?: string | null;
  sparklineRangeDays?: number;
  passRateAsSparklineCell?: boolean;
  frequencyAsSparklineCell?: boolean;
  avgDurationAsSparklineCell?: boolean;
  metricSeriesBySuiteId?: Map<
    string,
    {
      dates: Date[];
      passRate: number[];
      frequencyPerDay: number[];
      averageDurationMs: number[];
    }
  >;
  selectedProjectId: string | null;
  onProjectSelect: (projectId: string) => void;
  onStatusSelect?: (status: SuiteExecutionStatus | null | undefined) => void;
  thresholds: MetricThresholdGroup;
  resolveThresholds: (row: { projectId: string }) => MetricThresholdGroup | undefined;
  showDeltas?: boolean;
  disableLinks?: boolean;
};

const columnFlex = {
  name: 1,
  latest: 0.26,
  lastExecuted: 0.25,
  latestTests: 1,
  passRate: 0.75,
  frequency: 0.75,
  avgDuration: 0.75,
  flakeScore: 0.5,
  mttf: 0.5,
  delta: 0.4,
  tags: 0.75,
  actions: 0.1,
};

export const buildSuitesColumns = ({
  includeLatestTestsColumn = true,
  includeLastExecutedColumn = true,
  latestAsMeta = false,
  includeTagsInNameColumn = false,
  includeTagsColumn = true,
  includeActionsColumn = false,
  onDeleteRow,
  deleteRowPendingId = null,
  tagColorByName = {},
  onUpdateRowTagsColor,
  updateRowTagsColorPendingId = null,
  sparklineRangeDays: _sparklineRangeDays = 7,
  passRateAsSparklineCell = false,
  frequencyAsSparklineCell = false,
  avgDurationAsSparklineCell = false,
  metricSeriesBySuiteId,
  hideLatestPies = false,
  selectedProjectId,
  onProjectSelect,
  onStatusSelect,
  thresholds,
  resolveThresholds,
  showDeltas = true,
  disableLinks = false,
}: SuitesColumnsOptions): Array<GridColDef<SuiteListItemDto>> => {
  const stringOperators = getStringFilterOperators();
  const deltasEnabled = showDeltas;
  const resolvedThresholds = (row: SuiteListItemDto): MetricThresholdGroup => {
    if (selectedProjectId) return thresholds;
    return resolveThresholds(row) ?? thresholds;
  };
  const resolveTagTone = (tag: string): TagColor => {
    const trimmed = tag.trim();
    const fallback = trimmed.toLowerCase();
    if (Object.prototype.hasOwnProperty.call(tagColorByName, trimmed)) {
      return tagColorByName[trimmed];
    }
    if (Object.prototype.hasOwnProperty.call(tagColorByName, fallback)) {
      return tagColorByName[fallback];
    }
    return 'neutral';
  };
  const metricKeys: MetricColumnKey[] = [
    'passRate',
    'frequency',
    'avgDuration',
    ...(isMetricVisible('flakeScore') ? (['flakeScore'] as const) : []),
    ...(isMetricVisible('mttf') ? (['mttf'] as const) : []),
  ];
  const enabledMetricKeys = metricKeys.filter((key) => {
    if (passRateAsSparklineCell && key === 'passRate') return false;
    if (frequencyAsSparklineCell && key === 'frequency') return false;
    if (avgDurationAsSparklineCell && key === 'avgDuration') return false;
    return true;
  });

  const nameColumn: GridColDef<SuiteListItemDto> = {
    field: 'name',
    headerName: 'Suite',
    flex: columnFlex.name,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'tests-table-clone-test-cell' : undefined,
    filterOperators: stringOperators,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      if (latestAsMeta) {
        const tags = resolveEntityTags(params.row);
        const suiteHref = disableLinks ? null : `/suites/${params.row.slug}`;
        return (
          <CloneCellContainer gap="none" pt="4px">
            {suiteHref ? (
              <Text role="body" sx={{ width: '100%', minWidth: 0 }}>
                <Link
                  component={RouterLink}
                  to={suiteHref}
                  underline="hover"
                  color="inherit"
                  onClick={(event) => {
                    event.stopPropagation();
                    onProjectSelect(params.row.projectId);
                  }}
                  sx={{
                    display: 'inline-block',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {params.row.name}
                </Link>
              </Text>
            ) : (
              <Text role="body" truncate sx={{ display: 'block', width: '100%', minWidth: 0 }}>
                {params.row.name}
              </Text>
            )}
            {includeTagsInNameColumn && tags.length > 0 ? (
              <Box sx={{ width: '100%', minWidth: 0, mt: SUITE_NAME_TO_TAGS_GAP }}>
                <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
                  {tags.map((tag) => (
                    <Chip
                      key={tag}
                      size="xs"
                      tone={resolveTagTone(tag)}
                      label={tag}
                      sx={{ borderRadius: r('pill') }}
                    />
                  ))}
                </Inline>
              </Box>
            ) : null}
          </CloneCellContainer>
        );
      }
      return (
        <TableTextCell
          primary={{
            label: params.row.name,
            ...(disableLinks
              ? {}
              : {
                  href: `/suites/${params.row.slug}`,
                  onClick: () => {
                    onProjectSelect(params.row.projectId);
                  },
                }),
          }}
        />
      );
    },
  };

  const latestSummaryColumn: GridColDef<SuiteListItemDto> = {
    field: 'latestExecutionStatus',
    headerName: latestAsMeta ? 'Latest' : 'Lastest Status',
    flex: latestAsMeta ? undefined : columnFlex.latest,
    width: latestAsMeta ? 168 : undefined,
    align: 'left',
    headerAlign: 'left',
    cellClassName: latestAsMeta ? 'tests-table-clone-latest-cell' : undefined,
    filterable: false,
    sortable: true,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const isLoadingRow = (params.row as { __loading?: boolean }).__loading === true;
      if (isLoadingRow) {
        return null;
      }
      const latestExecution = params.row.latestExecution;
      const status = latestExecution?.status ?? null;
      if (latestAsMeta) {
        const statusLabel = formatStatusLabel(status) || '-';
        const statusTone = resolveStatusChipTone(status);
        const durationLabel =
          latestExecution?.durationMs !== null && latestExecution?.durationMs !== undefined
            ? formatDuration(latestExecution.durationMs)
            : '';
        const timestamp = latestExecution?.timestamp ?? null;
        const timestampLabel = timestamp ? formatRelativeTime(timestamp) : '';
        const timestampTitle = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
        const resolvedTimestampTitle =
          timestampTitle || (timestamp instanceof Date ? timestamp.toISOString() : timestamp);
        const runNumber = latestExecution?.runNumber ?? null;
        const executionHref =
          runNumber !== null && params.row.slug && !disableLinks
            ? `/suites/${params.row.slug}/runs/${runNumber}`
            : null;
        const counts = params.row.latestTestResults?.counts;
        const passed = counts?.passed ?? 0;
        const failed = counts?.failed ?? 0;
        const errored = counts?.errored ?? 0;
        const skipped = counts?.skipped ?? 0;
        const untested = counts?.untested ?? 0;
        const total = passed + failed + errored + skipped + untested;
        const pieSize = Number.parseFloat(s('3xl'));
        const latestStatusWidth = `calc(${s('3xl')} + ${s('2xl')} + ${s('xl')} + ${s('sm')})`;
        const latestPieWidth = s('3xl');
        const pieData =
          total > 0
            ? [
                { name: 'Passed', value: passed, status: 'passed' as const },
                { name: 'Failed', value: failed, status: 'failed' as const },
                { name: 'Errored', value: errored, status: 'errored' as const },
                { name: 'Skipped', value: skipped, status: 'skipped' as const },
                { name: 'Untested', value: untested, status: 'untested' as const },
              ].filter((slice) => slice.value > 0)
            : [];
        const showPie = !hideLatestPies && pieData.length > 0;
        const showPieMeta = !hideLatestPies;
        return (
          <Box
            sx={{
              width: '100%',
              minWidth: 0,
              display: 'grid',
              gridTemplateColumns: `${latestStatusWidth} ${latestPieWidth}`,
              columnGap: s('sm'),
              alignItems: 'start',
            }}
          >
            <Box
              sx={{
                width: latestStatusWidth,
                minWidth: latestStatusWidth,
              }}
            >
              <CloneCellContainer gap="none" pt="4px">
                <Chip
                  size="xs"
                  icon={<Box component={getStatusIconComponent(status)} />}
                  label={
                    executionHref ? (
                      <Link
                        component={RouterLink}
                        to={executionHref}
                        underline="hover"
                        color="inherit"
                        onClick={(event) => {
                          event.stopPropagation();
                          onProjectSelect(params.row.projectId);
                        }}
                      >
                        {statusLabel}
                      </Link>
                    ) : (
                      statusLabel
                    )
                  }
                  tone={statusTone}
                  sx={{
                    mt: s('none'),
                    mb: `calc(${s('sm')} + ${s('xxs')})`,
                    borderRadius: r('pill'),
                    backgroundColor: (theme) => resolveLatestChipBackground(theme, statusTone),
                    color: (theme) => resolveLatestChipForeground(theme, statusTone),
                    '& .MuiChip-label': {
                      color: 'inherit',
                      fontWeight: 500,
                      fontSize: '13px !important',
                    },
                    '& .MuiChip-label .MuiTypography-root': {
                      color: 'inherit',
                      fontWeight: 'inherit',
                      fontSize: '13px !important',
                    },
                    '& .MuiChip-label a': {
                      color: 'inherit',
                      fontWeight: 'inherit',
                      fontSize: '13px !important',
                    },
                    '& .MuiChip-label *': {
                      fontSize: '13px !important',
                    },
                    '& .MuiChip-icon': {
                      color: 'inherit',
                      fontSize: (theme) => theme.app.iconSizes.sm,
                      marginRight: '-6px',
                    },
                  }}
                />
                {durationLabel ? (
                  <Text
                    role="captionSmall"
                    color="textSecondary"
                    dataGridTypography="custom"
                    sx={LATEST_META_DETAIL_TEXT_SX}
                  >
                    <Box component="span" sx={LATEST_META_DETAIL_ROW_SX}>
                      <Tooltip title="Duration" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                        <TimerOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                      </Tooltip>
                      <Box component="span" sx={LATEST_META_DETAIL_VALUE_SX}>
                        {durationLabel}
                      </Box>
                    </Box>
                  </Text>
                ) : null}
                {timestampLabel ? (
                  <Text
                    role="captionSmall"
                    color="textSecondary"
                    dataGridTypography="custom"
                    sx={[LATEST_META_DETAIL_TEXT_SX, { mt: s('xs') }]}
                  >
                    <Box component="span" sx={LATEST_META_DETAIL_ROW_SX}>
                      <Tooltip title="Timestamp" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                        <EventOutlinedIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                      </Tooltip>
                      <Tooltip
                        title={resolvedTimestampTitle}
                        disableHoverListener={!timestamp}
                        arrow
                        slotProps={CLONE_TOOLTIP_SLOT_PROPS}
                      >
                        <Box component="span" sx={LATEST_META_DETAIL_VALUE_SX}>
                          {timestampLabel}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Text>
                ) : null}
              </CloneCellContainer>
            </Box>
            <Box
              className="suite-latest-pie"
              sx={{
                width: latestPieWidth,
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: s('xxs'),
                pt: s('none'),
                mt: '-2px',
              }}
            >
              {showPie ? (
                <SuiteLatestStatusPie
                  segments={pieData}
                  size={pieSize}
                  resolveSliceColor={(theme, sliceStatus) =>
                    resolveLatestChipBackground(theme, resolveStatusChipTone(sliceStatus))
                  }
                />
              ) : null}
              {showPieMeta ? (
                <Text
                  role="captionSmall"
                  color="textSecondary"
                  dataGridTypography="custom"
                  sx={LATEST_META_DETAIL_TEXT_SX}
                >
                  <Box component="span" sx={LATEST_META_DETAIL_ROW_SX}>
                    <Tooltip title="Tests" arrow slotProps={CLONE_TOOLTIP_SLOT_PROPS}>
                      <TestsIcon sx={{ fontSize: (theme) => theme.app.iconSizes.sm }} />
                    </Tooltip>
                    <Box component="span" sx={LATEST_META_DETAIL_VALUE_SX}>
                      {total.toLocaleString()} {total === 1 ? 'test' : 'tests'}
                    </Box>
                  </Box>
                </Text>
              ) : null}
            </Box>
          </Box>
        );
      }
      const isClickable = Boolean(status && onStatusSelect);
      const label = status ? formatStatusLabel(status) : 'Untested';
      const runNumber = latestExecution?.runNumber ?? null;
      const executionHref =
        runNumber !== null && params.row.slug
          ? `/suites/${params.row.slug}/runs/${runNumber}`
          : null;
      const labelNode =
        executionHref && !disableLinks ? (
          <Link
            component={RouterLink}
            to={executionHref}
            underline="hover"
            color="inherit"
            onClick={(event) => {
              event.stopPropagation();
              onProjectSelect(params.row.projectId);
            }}
          >
            <Text role="body" component="span" color="inherit">
              {label}
            </Text>
          </Link>
        ) : (
          label
        );
      if (isClickable && !executionHref) {
        return (
          <Box
            component="span"
            sx={{ cursor: 'pointer' }}
            onClick={() => {
              onStatusSelect?.(status ?? null);
            }}
          >
            <Text
              role="body"
              component="span"
              sx={{
                color: (theme) => getStatusTextColor(theme, status),
              }}
              transform="uppercase"
            >
              {labelNode}
            </Text>
          </Box>
        );
      }
      return (
        <Text
          role="body"
          component="span"
          sx={{
            color: (theme) => getStatusTextColor(theme, status),
          }}
          transform="uppercase"
        >
          {labelNode}
        </Text>
      );
    },
  };
  const lastExecutedColumn: GridColDef<SuiteListItemDto, Date | string | null> = {
    field: 'latestExecutionTimestamp',
    headerName: 'Last Executed',
    flex: columnFlex.lastExecuted,
    align: 'right',
    headerAlign: 'right',
    filterable: false,
    valueGetter: (_value, row) => row.latestExecution?.timestamp ?? null,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto, Date | string | null>) => {
      const timestamp = params.value ?? null;
      const label = timestamp ? formatRelativeTime(timestamp) : '-';
      const fullTimestamp = timestamp ? formatDate(timestamp, { includeTime: true }) : '';
      return (
        <Tooltip
          title={fullTimestamp}
          disableHoverListener={!fullTimestamp}
          arrow
          slotProps={{
            tooltip: {
              sx: {
                fontSize: (theme) => theme.typography.body2.fontSize,
                lineHeight: (theme) => theme.typography.body2.lineHeight,
              },
            },
          }}
        >
          <Box component="span" sx={{ display: 'block', width: '100%' }}>
            <Text role="body" component="span" sx={RELATIVE_TIME_TEXT_SX}>
              {label.length > 0 ? label : '-'}
            </Text>
          </Box>
        </Tooltip>
      );
    },
  };

  const latestTestsColumn: GridColDef<SuiteListItemDto> = {
    field: 'testLatestExecutionPassed',
    headerName: 'Latest Tests',
    flex: columnFlex.latestTests,
    align: 'center',
    headerAlign: 'center',
    filterable: false,
    sortable: false,
    renderCell: (params) => {
      const counts = params.row.latestTestResults?.counts;
      const passed = counts?.passed ?? 0;
      const failed = counts?.failed ?? 0;
      const errored = counts?.errored ?? 0;
      const skipped = counts?.skipped ?? 0;
      const untested = counts?.untested ?? 0;
      const total = passed + failed + errored + skipped + untested;
      const segments = [
        { label: 'Passed', value: passed, color: uiTokens.statusColors.green },
        { label: 'Failed', value: failed, color: uiTokens.statusColors.red },
        { label: 'Errored', value: errored, color: uiTokens.statusColors.brown },
        { label: 'Skipped', value: skipped, color: uiTokens.statusColors.orange },
        { label: 'Untested', value: untested, color: uiTokens.statusColors.neutral },
      ];
      return <StackedCountBar segments={segments} total={total} totalLabel={`Tests: ${total}`} />;
    },
  };

  const { passRateSparklineColumn, frequencySparklineColumn, avgDurationSparklineColumn } =
    buildSuitesSparklineColumns({
      passRateAsSparklineCell,
      frequencyAsSparklineCell,
      avgDurationAsSparklineCell,
      showDeltas,
      resolvedThresholds,
      metricSeriesBySuiteId,
      columnFlex: {
        passRate: columnFlex.passRate,
        frequency: columnFlex.frequency,
        avgDuration: columnFlex.avgDuration,
      },
    });

  const metricColumns = buildMetricColumns<SuiteListItemDto>({
    enabledKeys: enabledMetricKeys,
    includeDeltas: (key) => {
      if (!deltasEnabled) return false;
      if (passRateAsSparklineCell && key === 'passRate') return false;
      if (frequencyAsSparklineCell && key === 'frequency') return false;
      if (avgDurationAsSparklineCell && key === 'avgDuration') return false;
      if (key === 'flakeScore') return isMetricDeltaVisible('flakeScore');
      if (key === 'mttf') return isMetricDeltaVisible('mttf');
      return true;
    },
    fields: {
      passRate: 'metrics.passRate',
      frequency: 'metrics.frequencyPerDay',
      avgDuration: 'metrics.averageDurationMs',
      flakeScore: 'metrics.flakeScore',
      mttf: 'metrics.mttfSeconds',
    },
    deltaFields: {
      passRate: 'metrics.delta.passRate',
      frequency: 'metrics.delta.frequencyPerDay',
      avgDuration: 'metrics.delta.averageDurationMs',
      flakeScore: 'metrics.delta.flakeScore',
      mttf: 'metrics.delta.mttfSeconds',
    },
    flex: {
      passRate: columnFlex.passRate,
      frequency: columnFlex.frequency,
      avgDuration: columnFlex.avgDuration,
      flakeScore: columnFlex.flakeScore,
      mttf: columnFlex.mttf,
    },
    deltaFlex: {
      passRate: columnFlex.delta,
      frequency: columnFlex.delta,
      avgDuration: columnFlex.delta,
      flakeScore: columnFlex.delta,
      mttf: columnFlex.delta,
    },
    thresholds: thresholds,
    resolveThresholds: (row) => resolvedThresholds(row),
    align: 'right',
    headerAlign: 'right',
  });

  const tagsColumn: GridColDef<SuiteListItemDto> = {
    field: 'tags',
    headerName: 'Tags',
    flex: columnFlex.tags,
    filterable: false,
    sortable: false,
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => {
      const tags = resolveEntityTags(params.row);
      if (tags.length === 0) {
        return (
          <Text role="body" color="textSecondary">
            -
          </Text>
        );
      }
      return (
        <Inline gap="sm" wrap="wrap" align="center" sx={{ width: '100%', minWidth: 0 }}>
          {tags.map((tag) => (
            <Chip
              key={tag}
              size="xs"
              tone={resolveTagTone(tag)}
              label={tag}
              sx={{ borderRadius: r('pill') }}
            />
          ))}
        </Inline>
      );
    },
  };

  const actionsColumn: GridColDef<SuiteListItemDto> = {
    field: 'actions',
    headerName: '',
    flex: columnFlex.actions,
    minWidth: 56,
    filterable: false,
    sortable: false,
    disableColumnMenu: true,
    align: 'right',
    headerAlign: 'right',
    renderCell: (params: GridRenderCellParams<SuiteListItemDto>) => (
      <CloneActionsCell
        rowName={params.row.name}
        editTagsEntityNoun="suite"
        rowTags={resolveEntityTags(params.row)}
        rowTagColors={resolveEntityTags(params.row).map((tag) => tagColorByName[tag] ?? 'neutral')}
        onUpdateTagsColor={(input) => {
          onUpdateRowTagsColor?.({ row: params.row, ...input });
        }}
        onDelete={() => {
          onDeleteRow?.(params.row);
        }}
        disableDelete={deleteRowPendingId === params.row.id}
        updateTagsColorLoading={updateRowTagsColorPendingId === params.row.id}
      />
    ),
  };

  return [
    nameColumn,
    latestSummaryColumn,
    ...(includeLatestTestsColumn ? [latestTestsColumn] : []),
    ...(includeLastExecutedColumn ? [lastExecutedColumn] : []),
    ...(passRateSparklineColumn ? [passRateSparklineColumn] : []),
    ...(frequencySparklineColumn ? [frequencySparklineColumn] : []),
    ...(avgDurationSparklineColumn ? [avgDurationSparklineColumn] : []),
    ...metricColumns,
    ...(includeTagsColumn ? [tagsColumn] : []),
    ...(includeActionsColumn ? [actionsColumn] : []),
  ];
};
